import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Skeleton} from 'src/design/atoms/Skeleton';
import {Text} from 'src/design/atoms/Text';
import {IconRefresh} from '@tabler/icons-react';
import {useCallback, useEffect, useRef, useState} from 'react';
import ReactPlayer from 'react-player';
import {Button} from 'src/design/atoms/Button';
import {VideoControlBar} from 'src/design/organisms/VideoControlBar';
import {useFlushOnHide} from 'src/shared/hooks/useFlushOnHide';
import {useVideoSeekGuard} from 'src/shared/hooks/useVideoSeekGuard';
import {useGetCourseVideoUrlQuery} from 'src/redux/queries/courseVideo';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import {reportVideoError} from 'src/shared/utils/videoTelemetry';

// iPadOS reports a desktop Mac UA, so also treat touch-capable Macs as iOS.
function isIOS(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  if (/iPhone|iPod|iPad/.test(ua)) return true;
  return /Mac/.test(ua) && navigator.maxTouchPoints > 1;
}

export interface InternalVideoPlayerProps {
  videoId?: number;
  enableVideoControls: boolean;
  enableVideoAutostart: boolean;
  enableVideoResume?: boolean;
  isVideoPaused?: boolean;
  currentProgressSeconds?: number;
  onVideoTimeEventUpdate?: (seconds: number) => void;
  onVideoEnd?: (seconds: number) => void;
}

export function mediaErrorMessage(code?: number): string {
  switch (code) {
    case 2:
      return 'The video stopped because of a network problem. Check your connection and reload.'; // MEDIA_ERR_NETWORK
    case 3:
      return 'This video could not be played on your device. Try reloading or another browser.'; // MEDIA_ERR_DECODE
    case 4:
      return 'This video format is not supported by your browser. Try reloading.'; // MEDIA_ERR_SRC_NOT_SUPPORTED
    default:
      return 'The video could not be played. Please reload and try again.';
  }
}

export const InternalVideoPlayer: React.FC<InternalVideoPlayerProps> = ({
  videoId,
  enableVideoControls,
  enableVideoAutostart,
  enableVideoResume,
  isVideoPaused,
  currentProgressSeconds,
  onVideoTimeEventUpdate,
  onVideoEnd,
}) => {
  const {data, isLoading, isFetching, isError, error, refetch} =
    useGetCourseVideoUrlQuery(Number(videoId), {
      skip: !videoId,
    });

  useEffect(() => {
    if (!data?.expirationDate || isLoading || isFetching) return;

    const expirationDate = new Date(data.expirationDate);
    const now = new Date();

    // We refetch the video if it expires in the next 5 minutes.
    const isExpiring =
      expirationDate < now &&
      now < new Date(expirationDate.getTime() + 1000 * 60 * 5);
    if (isExpiring) {
      void refetch();
    }
  }, [data?.expirationDate, isLoading, isFetching, refetch]);

  const playerRef = useRef<HTMLVideoElement | null>(null);
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasRestoredPosition, setHasRestoredPosition] = useState(false);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const onChange = () =>
      setIsFullscreen(
        !!containerRef.current &&
          document.fullscreenElement === containerRef.current
      );
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const setPlayerRef = useCallback((el: HTMLVideoElement | null) => {
    playerRef.current = el;
    setVideoEl(el);
  }, []);

  const {getMaxPlayedSeconds} = useVideoSeekGuard(videoEl, {
    initialMaxSeconds: currentProgressSeconds ?? 0,
  });

  // Browsers block unmuted autoplay, so autostart must start muted.
  const [muted, setMuted] = useState(enableVideoAutostart);
  const [volume, setVolume] = useState(1);
  const [uiSeconds, setUiSeconds] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    if (!videoEl) return;
    videoEl.muted = muted;
    videoEl.volume = volume;
  }, [videoEl, muted, volume]);

  // Manage internal playing state for controls to work properly, since ReactPlayer becomes a controlled component when using the playing prop.
  const [isPlaying, setIsPlaying] = useState(enableVideoAutostart);

  // isVideoPaused=false means the active-screen monitor is not forcing a pause; it
  // must only resume a video the monitor itself paused, never start a stopped one.
  const pausedByMonitorRef = useRef(false);
  useEffect(() => {
    if (isVideoPaused === undefined) return;
    if (isVideoPaused) {
      setIsPlaying(playing => {
        if (playing) pausedByMonitorRef.current = true;
        return false;
      });
    } else if (pausedByMonitorRef.current) {
      pausedByMonitorRef.current = false;
      setIsPlaying(true);
    }
  }, [isVideoPaused]);

  // Reset position restoration flag and clear any media error when video changes
  useEffect(() => {
    setHasRestoredPosition(false);
    setMediaError(null);
  }, [data?.videoUrl]);

  const handleReady = () => {
    setMediaError(null);
    if (
      !hasRestoredPosition &&
      currentProgressSeconds &&
      currentProgressSeconds > 0 &&
      playerRef.current
    ) {
      playerRef.current.currentTime = currentProgressSeconds;
      setHasRestoredPosition(true);
    }
  };

  const handlePlay = () => {
    setIsPlaying(true);
  };

  const handlePause = () => {
    setIsPlaying(false);
  };

  const handleError = () => {
    setMediaError(mediaErrorMessage(playerRef.current?.error?.code));
    reportVideoError('internal', playerRef.current?.error);
  };

  // If enableVideoResume is enabled, we'll track the video progress so we can resume it later.
  const [playedSeconds, setPlayedSeconds] = useState(0);
  const handleTimeUpdate = (event: any) => {
    const target = event.target as HTMLVideoElement;
    const seconds = target.currentTime;

    setUiSeconds(seconds);

    // We only care about 2 second intervals.
    // So floor the current time and compare to previous time + 4, which should be a 5 second difference.
    if (enableVideoResume && Math.floor(seconds) > playedSeconds + 4) {
      setPlayedSeconds(seconds);

      if (onVideoTimeEventUpdate) {
        onVideoTimeEventUpdate(seconds);
      }
    }
  };

  const handleDurationChange = (event: any) => {
    const target = event.target as HTMLVideoElement;
    setDuration(target.duration || 0);
  };

  const handleSeek = (seconds: number) => {
    if (videoEl) {
      videoEl.currentTime = Math.min(seconds, getMaxPlayedSeconds());
    }
  };

  const handleFullscreen = () => {
    const container = containerRef.current;
    // Fullscreen requests reject when the browser or user denies them; there is
    // no recoverable action, so the rejection is logged rather than surfaced.
    const onDenied = (error: unknown) =>
      console.warn('Fullscreen denied', error);
    if (document.fullscreenElement === container) {
      document.exitFullscreen?.().catch(onDenied);
    } else if (container?.requestFullscreen) {
      container.requestFullscreen().catch(onDenied);
    } else {
      (videoEl as any)?.webkitEnterFullscreen?.();
    }
  };

  const handleVideoEnd = (event: any) => {
    const target = event.target as HTMLVideoElement;
    const seconds = target.currentTime;

    if (onVideoEnd) {
      onVideoEnd(seconds);
    }
  };

  const getFlushValue = useCallback(() => playerRef.current?.currentTime, []);
  const handleFlush = useCallback(
    (s: number) => {
      if (enableVideoResume && onVideoTimeEventUpdate)
        onVideoTimeEventUpdate(s);
    },
    [enableVideoResume, onVideoTimeEventUpdate]
  );
  useFlushOnHide(getFlushValue, handleFlush);

  return (
    <Box>
      {(isLoading || isFetching) && <Skeleton animate={true} height={400} />}
      {isError && (
        <Alert color="red" title="Error">
          <Text>
            {parseErrorMessage(error) ??
              'An error occurred while fetching the video stream.'}
          </Text>
        </Alert>
      )}
      {mediaError && (
        <Alert color="red" title="Video playback error" variant="light" mb="xs">
          <Text size="sm">{mediaError}</Text>
        </Alert>
      )}
      {data && (
        <Box
          ref={containerRef}
          style={
            isFullscreen
              ? {
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100vh',
                  backgroundColor: 'black',
                }
              : undefined
          }>
          <ReactPlayer
            ref={setPlayerRef}
            src={data.videoUrl}
            width="100%"
            height="100%"
            style={isFullscreen ? {flex: 1, minHeight: 0} : undefined}
            controls={false}
            pip={false}
            autoPlay={enableVideoAutostart}
            playing={isPlaying}
            muted={muted}
            playsInline={true}
            loop={false}
            onReady={handleReady}
            onPlay={handlePlay}
            onPause={handlePause}
            onError={handleError}
            onTimeUpdate={handleTimeUpdate}
            onDurationChange={handleDurationChange}
            onEnded={onVideoEnd ? handleVideoEnd : undefined}
          />
          <VideoControlBar
            isPlaying={isPlaying}
            onPlayPause={() => setIsPlaying(p => !p)}
            muted={muted}
            onToggleMute={() => setMuted(m => !m)}
            volume={volume}
            onVolumeChange={setVolume}
            showVolumeSlider={!isIOS()}
            currentSeconds={uiSeconds}
            durationSeconds={duration}
            maxSeekableSeconds={getMaxPlayedSeconds()}
            showSeekBar={enableVideoControls}
            onSeek={handleSeek}
            onFullscreen={handleFullscreen}
            isFullscreen={isFullscreen}
          />
        </Box>
      )}

      <Group justify="flex-end" mt="xs">
        <Button
          color="gray"
          variant="subtle"
          size="xsm"
          onClick={refetch}
          loading={isFetching || isLoading}
          leftSection={<IconRefresh size={16} />}>
          Reload Video
        </Button>
      </Group>
    </Box>
  );
};
